import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { RefreshCw, CheckCircle, XCircle, Clock, Database, AlertTriangle } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

interface RecoveryStats {
  total_failures: number;
  resolved_count: number;
  pending_count: number;
  no_payload_count: number;
  recently_resolved: number;
  latest_resolution: string | null;
}

export const WebhookRecoveryProgress = () => {
  const [stats, setStats] = useState<RecoveryStats | null>(null);
  const [loading, setLoading] = useState(false);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const { toast } = useToast();

  const fetchRecoveryStats = async () => {
    setLoading(true);
    try {
      // Get total failures since Jan 16th
      const { count: totalFailures, error: totalError } = await supabase
        .from('webhook_failures')
        .select('*', { count: 'exact', head: true })
        .eq('webhook_type', 'obsync')
        .eq('failure_stage', 'database_insert')
        .gte('created_at', '2026-01-16T00:00:00Z');

      if (totalError) throw totalError;

      // Get resolved count
      const { count: resolvedCount, error: resolvedError } = await supabase
        .from('webhook_failures')
        .select('*', { count: 'exact', head: true })
        .eq('webhook_type', 'obsync')
        .eq('failure_stage', 'database_insert')
        .not('resolved_at', 'is', null)
        .gte('created_at', '2026-01-16T00:00:00Z');

      if (resolvedError) throw resolvedError;

      // Get pending (unresolved with payload)
      const { count: pendingCount, error: pendingError } = await supabase
        .from('webhook_failures')
        .select('*', { count: 'exact', head: true })
        .eq('webhook_type', 'obsync')
        .eq('failure_stage', 'database_insert')
        .is('resolved_at', null)
        .not('payload_storage_path', 'is', null)
        .gte('created_at', '2026-01-16T00:00:00Z');

      if (pendingError) throw pendingError;

      // Get no payload count
      const { count: noPayloadCount, error: noPayloadError } = await supabase
        .from('webhook_failures')
        .select('*', { count: 'exact', head: true })
        .eq('webhook_type', 'obsync')
        .eq('failure_stage', 'database_insert')
        .is('resolved_at', null)
        .is('payload_storage_path', null)
        .gte('created_at', '2026-01-16T00:00:00Z');

      if (noPayloadError) throw noPayloadError;

      // Get recently resolved (last 10 minutes)
      const { count: recentlyResolved, error: recentCountError } = await supabase
        .from('webhook_failures')
        .select('*', { count: 'exact', head: true })
        .eq('webhook_type', 'obsync')
        .eq('failure_stage', 'database_insert')
        .not('resolved_at', 'is', null)
        .gte('resolved_at', new Date(Date.now() - 10 * 60 * 1000).toISOString());

      if (recentCountError) throw recentCountError;

      // Get latest resolution timestamp
      const { data: latestData, error: latestError } = await supabase
        .from('webhook_failures')
        .select('resolved_at')
        .eq('webhook_type', 'obsync')
        .eq('failure_stage', 'database_insert')
        .not('resolved_at', 'is', null)
        .order('resolved_at', { ascending: false })
        .limit(1)
        .single();

      if (latestError && latestError.code !== 'PGRST116') throw latestError; // PGRST116 = no rows returned

      const latestResolution = latestData?.resolved_at || null;

      setStats({
        total_failures: totalFailures || 0,
        resolved_count: resolvedCount || 0,
        pending_count: pendingCount || 0,
        no_payload_count: noPayloadCount || 0,
        recently_resolved: recentlyResolved || 0,
        latest_resolution: latestResolution
      });

    } catch (error) {
      console.error('Error fetching recovery stats:', error);
      toast({
        title: "Error",
        description: "Failed to fetch recovery statistics",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRecoveryStats();

    // Auto-refresh every 10 seconds if enabled
    let interval: NodeJS.Timeout | null = null;
    if (autoRefresh) {
      interval = setInterval(() => {
        fetchRecoveryStats();
      }, 10000);
    }

    return () => {
      if (interval) clearInterval(interval);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoRefresh]);

  if (!stats) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Webhook Recovery Progress</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-8">
            <RefreshCw className="h-8 w-8 mx-auto mb-4 animate-spin text-gray-400" />
            <p className="text-sm text-gray-500">Loading recovery statistics...</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  const progressPercentage = stats.total_failures > 0 
    ? (stats.resolved_count / stats.total_failures) * 100 
    : 0;

  const recoverableCount = stats.total_failures - stats.no_payload_count;
  const recoverableProgress = recoverableCount > 0
    ? (stats.resolved_count / recoverableCount) * 100
    : 0;

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Database className="h-5 w-5 text-blue-600" />
              Webhook Recovery Progress
            </CardTitle>
            <CardDescription className="mt-1">
              Monitoring recovery of webhook failures since January 16th, 2026
            </CardDescription>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setAutoRefresh(!autoRefresh)}
            >
              <RefreshCw className={`h-4 w-4 mr-2 ${autoRefresh ? 'animate-spin' : ''}`} />
              {autoRefresh ? 'Auto-refresh ON' : 'Auto-refresh OFF'}
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={fetchRecoveryStats}
              disabled={loading}
            >
              <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Overall Progress */}
        <div className="space-y-2">
          <div className="flex items-center justify-between text-sm">
            <span className="font-medium">Overall Progress</span>
            <span className="text-gray-600">
              {stats.resolved_count.toLocaleString()} / {stats.total_failures.toLocaleString()} resolved
            </span>
          </div>
          <Progress value={progressPercentage} className="h-3" />
          <div className="flex items-center justify-between text-xs text-gray-500">
            <span>{progressPercentage.toFixed(1)}% complete</span>
            <span>{stats.pending_count.toLocaleString()} pending</span>
          </div>
        </div>

        {/* Recoverable Progress */}
        <div className="space-y-2">
          <div className="flex items-center justify-between text-sm">
            <span className="font-medium">Recoverable Progress</span>
            <span className="text-gray-600">
              {stats.resolved_count.toLocaleString()} / {recoverableCount.toLocaleString()} recoverable
            </span>
          </div>
          <Progress value={recoverableProgress} className="h-2" />
          <div className="text-xs text-gray-500">
            {recoverableProgress.toFixed(1)}% of recoverable failures processed
          </div>
        </div>

        {/* Statistics Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="space-y-1">
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <Database className="h-4 w-4" />
              Total Failures
            </div>
            <div className="text-2xl font-bold">{stats.total_failures.toLocaleString()}</div>
          </div>

          <div className="space-y-1">
            <div className="flex items-center gap-2 text-sm text-green-600">
              <CheckCircle className="h-4 w-4" />
              Resolved
            </div>
            <div className="text-2xl font-bold text-green-600">
              {stats.resolved_count.toLocaleString()}
            </div>
          </div>

          <div className="space-y-1">
            <div className="flex items-center gap-2 text-sm text-yellow-600">
              <Clock className="h-4 w-4" />
              Pending
            </div>
            <div className="text-2xl font-bold text-yellow-600">
              {stats.pending_count.toLocaleString()}
            </div>
          </div>

          <div className="space-y-1">
            <div className="flex items-center gap-2 text-sm text-red-600">
              <XCircle className="h-4 w-4" />
              No Payload
            </div>
            <div className="text-2xl font-bold text-red-600">
              {stats.no_payload_count.toLocaleString()}
            </div>
          </div>
        </div>

        {/* Recent Activity */}
        {stats.recently_resolved > 0 && (
          <div className="pt-4 border-t">
            <div className="flex items-center gap-2 text-sm text-green-600 mb-2">
              <RefreshCw className="h-4 w-4" />
              <span className="font-medium">Recent Activity (Last 10 minutes)</span>
            </div>
            <div className="flex items-center gap-4">
              <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                {stats.recently_resolved} resolved
              </Badge>
              {stats.latest_resolution && (
                <span className="text-xs text-gray-500">
                  Latest: {new Date(stats.latest_resolution).toLocaleTimeString()}
                </span>
              )}
            </div>
          </div>
        )}

        {/* Status Badge */}
        <div className="pt-4 border-t">
          <div className="flex items-center gap-2">
            {stats.pending_count === 0 ? (
              <>
                <CheckCircle className="h-5 w-5 text-green-600" />
                <span className="text-sm font-medium text-green-600">
                  Recovery Complete! All recoverable failures have been processed.
                </span>
              </>
            ) : (
              <>
                <AlertTriangle className="h-5 w-5 text-yellow-600" />
                <span className="text-sm font-medium text-yellow-600">
                  Recovery in progress... {stats.pending_count.toLocaleString()} failures remaining
                </span>
              </>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
